import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * PO-2026-06 — Admin Top-Nav DM Badge.
 *
 * A Messages button with a red unread count of DIRECT messages from talent.
 * Polls /admin/messages/dm/unread-count, refetches on window focus and when
 * any DM surface fires the `admin-dm-updated` event (so the count clears the
 * instant the admin opens/reads a conversation). Admin-only.
 */
const POLL_MS = 30000;

export const AdminDmBadge = ({ variant = 'dark' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const timer = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchCount = useCallback(async () => {
    try {
      const r = await api.get('/admin/messages/dm/unread-count');
      setCount(r.data?.unread || 0);
    } catch (e) {
      /* silent — badge is best-effort */
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    fetchCount();
    timer.current = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    const onUpdate = () => fetchCount();
    window.addEventListener('focus', onFocus);
    window.addEventListener('admin-dm-updated', onUpdate);
    return () => {
      if (timer.current) clearInterval(timer.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('admin-dm-updated', onUpdate);
    };
  }, [isAdmin, fetchCount]);

  if (!isAdmin) return null;

  const base =
    variant === 'nav'
      ? 'bg-[#03DFFC]/10 hover:bg-[#03DFFC]/20 text-[#03DFFC]'
      : 'text-slate-200 border border-slate-700/60 bg-slate-800/40 hover:bg-slate-800/70 hover:border-slate-600';

  return (
    <button
      onClick={() => navigate('/admin/messages')}
      className={`relative inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition ${base}`}
      data-testid="admin-dm-nav-badge"
      title={count > 0 ? `${count} unread direct message${count !== 1 ? 's' : ''}` : 'Direct messages'}
    >
      <MessageSquare className="w-4 h-4" />
      <span className="hidden sm:inline">Messages</span>
      {count > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none shadow-md ring-2 ring-slate-950"
          data-testid="admin-dm-unread-count"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

export default AdminDmBadge;
